<template>
	<section class="layout-full_width">
		<header class="header">
			<h1 class="title">
				{{ $t('agency.balance_management') }}
			</h1>

			<div class="top-panel">
				<nice-button-2
					icon="arrow-14"
					icon-painted
					:icon-state="90"
					@click="$router.push({ name: 'agency-list' })"
				>
					{{ $t('common.back') | capitalize }}
				</nice-button-2>
			</div>
		</header>

		<!-- Balance Table (agencies) -->
		<div v-if="isLoading" class="loading-message">
			Loading...
		</div>

		<table v-else class="nice-table balance-table">
			<thead>
				<tr>
					<th>{{ $t('agency.name') }}&nbsp;&mdash;</th>
					<th>{{ $t('agency.balance') }}&nbsp;&mdash;</th>
					<th>{{ $t('agency.prepaid_spent') }}&nbsp;&mdash;</th>
					<th>{{ $t('agency.credit') }}&nbsp;&mdash;</th>
					<th>{{ $t('agency.credit_spent') }}&nbsp;&mdash;</th>
					<th>&nbsp;</th>
				</tr>
			</thead>

			<tbody>
				<template
					v-for="agency in agencies"
				>
					<!-- Agency row -->
					<tr :key="agency.id">
						<td class="agency-name">{{ agency.name }}</td>

						<td class="agency-balance" v-html="formatCash(agency.balance, agency.currency, 2)" />

						<td class="agency-prepaid-spent" v-html="formatCash(agency.prepaid_spent, agency.currency, 2)" />

						<td class="agency-credit" v-html="formatCash(agency.credit, agency.currency, 2)" />

						<td class="agency-credit-spent" v-html="formatCash(agency.credit_spent, agency.currency, 2)" />

						<td class="agency-actions">
							<nice-button-2
								class="action-btn"
								bordered
								@click="openManageModal(agency)"
							>
								{{ $t('agency.manage_balance') }}
							</nice-button-2>
							<nice-button-2
								class="action-btn"
								:palette="historyAgencyId === agency.id ? 'main' : 'gray'"
								@click="toggleHistory(agency)"
							>
								{{ $t('agency.view_changes') }}
							</nice-button-2>
						</td>
					</tr>

					<!-- Inline change history for this agency -->
					<tr
						v-if="historyAgencyId === agency.id"
						:key="'history-' + agency.id"
						class="history-row"
					>
						<td colspan="6" class="history-cell">
							<div class="history-section">
								<div class="history-header">
									<h3 class="section-title">
										{{ $t('agency.change_history') }}: {{ agency.name }}
									</h3>

									<div class="date-filter">
										<label class="date-label">{{ $t('agency.date_from') }}</label>
										<input
											v-model="filterDateFrom"
											type="date"
											class="date-input"
											@change="onDateFilterChange"
										/>

										<label class="date-label">{{ $t('agency.date_to') }}</label>
										<input
											v-model="filterDateTo"
											type="date"
											class="date-input"
											@change="onDateFilterChange"
										/>

										<nice-button-2
											v-if="filterDateFrom || filterDateTo"
											class="clear-filter-btn"
											palette="gray"
											@click="clearDateFilter"
										>
											{{ $t('agency.clear_filter') }}
										</nice-button-2>
									</div>
								</div>

								<div v-if="changeHistory?.results?.length" class="history-scroll">
									<table class="nice-table history-table">
										<thead>
											<tr>
												<th>{{ $t('transactions.id') | capitalize }}</th>
												<th>{{ $t('agency.changed_field') }}</th>
												<th>{{ $t('transactions.amount')  | capitalize }} ({{ agency.currency }})</th>
												<th>{{ $t('agency.changed_by') }}</th>
												<th>{{ $t('agency.changed_at') }}</th>
												<th>{{ $t('transactions.description') | capitalize }}</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="(entry, idx) in changeHistory.results"
												:key="idx"
											>
												<td>{{ entry.id }}</td>

												<td v-if="entry.credit.account == agency.main_account_number || entry.debit.account == agency.main_account_number">balance</td>
												<td v-else-if="entry.credit.account == agency.credit_account_number || entry.debit.account == agency.credit_account_number">credit balance</td>
												<td v-else>&mdash;</td>

												<td
													v-if="entry.credit.account == agency.main_account_number || entry.credit.account == agency.credit_account_number"
													v-html="'-' + formatCash(entry.amount, historyAgencyCurrency, 2)"
													:style="{ color: $palette['red'] }" />
												<td
													v-else-if="entry.debit.account == agency.main_account_number || entry.debit.account == agency.credit_account_number"
													v-html="'+' + formatCash(entry.amount, historyAgencyCurrency, 2)"
													:style="{ color: $palette['green'] }" />
												<td
													v-else
													v-html="formatCash(entry.amount, historyAgencyCurrency, 2)" />

												<td class="col-user">{{ entry.created_by || '—' }}</td>
												<td class="col-date">{{ profileFormatDateTime(entry.ctime) }}</td>
												<td>{{ entry.description }}</td>
											</tr>
										</tbody>
									</table>
									<div
										v-if="changeHistory?.results?.length < changeHistory.count"
										class="paginate"
									>
										<a
											:class="{
												'hidden': currentPage == 1,
											}"
											@click="onPageChange(currentPage - 1)">&lt;</a>
										<a
											:class="{
												'hidden': currentPage == maxPage,
											}"
											@click="onPageChange(currentPage + 1)">&gt;</a>
									</div>
								</div>

								<div v-else-if="isLoadingHistory" class="empty-message">
									Loading...
								</div>

								<div v-else class="empty-message">
									{{ filterDateFrom || filterDateTo ? $t('agency.no_changes_in_period') : $t('agency.no_changes') }}
								</div>
							</div>
						</td>
					</tr>
				</template>

				<tr v-if="!agencies.length">
					<td colspan="6" class="empty-message">
						No agencies found
					</td>
				</tr>
			</tbody>
		</table>

		<!-- Manage Balance Modal -->
		<transition name="modal-transition">
			<nice-modal
				v-if="showManageModal"
				@close="closeManageModal"
			>
				<section class="manage-modal">
					<header class="manage-modal__header">
						<NiceIcon2
							class="manage-modal__icon"
							icon="money"
						/>
						<h2 class="manage-modal__title">
							{{ $t('agency.manage_balance') }} — {{ manageAgency.name }}
						</h2>
						<div class="manage-modal__actions">
							<nice-button-2
								bordered
								@click="closeManageModal"
							>
								{{ $t('common.close') | capitalize }}
							</nice-button-2>
						</div>
					</header>

					<div class="manage-modal__body">
						<div class="manage-modal__current">
							<div class="manage-modal__stat">
								<span class="manage-modal__label">{{ $t('agency.balance') }}</span>
								<span class="manage-modal__value" v-html="formatCash(manageAgency.balance, manageAgency.currency, 2)" />
							</div>
							<div class="manage-modal__stat">
								<span class="manage-modal__label">{{ $t('agency.credit') }}</span>
								<span class="manage-modal__value" v-html="formatCash(manageAgency.credit, manageAgency.currency, 2)" />
							</div>
						</div>

						<!-- Adjust balance -->
						<div class="manage-modal__section">
							<h3 class="manage-modal__section-title">
								{{ $t('agency.adjust_balance') }}
							</h3>
							<p class="manage-modal__hint">
								{{ $t('agency.adjust_balance_hint') }}
							</p>
							<div class="manage-modal__input-row">
								<input
									ref="balanceInput"
									v-model="balanceAmount"
									type="number"
									step="0.01"
									class="manage-modal__input"
									:placeholder="$t('agency.enter_amount')"
									@keyup.enter="submitBalance"
								/>
								<nice-button-2
									bordered
									palette="green"
									:disabled="isSaving || !balanceAmount"
									@click="submitBalance"
								>
									{{ $t('common.apply') | capitalize }}
								</nice-button-2>
							</div>
						</div>

					</div>
				</section>
			</nice-modal>
		</transition>
	</section>
</template>


<script>
/**
 * Balance Management Page
 *
 * Displays a list of DSP agencies with their balance and credit info.
 * - "Manage" opens a modal to adjust prepaid balance and credit limit.
 * - Prepaid balance uses existing addChildAgencyBalance API (positive = add, negative = subtract).
 * - "View Changes" shows per-agency change history with date filtering.
 */

import moment from 'moment';

import NiceModal from '@/ui/nice-modal';
import NiceIcon2 from '@/ui/nice-icon-2';

import { formatCash, profileFormatDateTime } from '@/utilites';
import { addChildAgencyBalance } from '@/api/agency';
import {
	requestAgenciesBalance,
	requestBalanceChangeHistory,
} from '@/api/agency-balance';


export default {
	name: 'PageAgencyBalance',

	components: {
		NiceModal,
		NiceIcon2,
	},

	data() {
		let today = moment().format('YYYY-MM-DD');

		return {
			agencies: [],
			isLoading: true,
			isSaving: false,

			// Manage modal
			showManageModal: false,
			manageAgency: null,
			balanceAmount: '',

			// History
			isLoadingHistory: false,
			historyAgencyId: null,
			changeHistory: {},
			currentPage: 1,
			pageSize: 20,

			today: today,
			filterDateFrom: today,
			filterDateTo: today,
		};
	},


	computed: {
		/**
		 * Currency of the agency whose history is currently shown
		 */
		historyAgencyCurrency() {
			if (!this.historyAgencyId) return '';
			const agency = this.agencies.find(a => a.id === this.historyAgencyId);
			return agency ? agency.currency : '';
		},

		maxPage() {
			const total = this.changeHistory.count || 0;
			return Math.ceil(total / this.pageSize);
		},

	},


	methods: {
		formatCash,
		profileFormatDateTime,

		/**
		 * Clear date filter and re-fetch history without date constraints
		 */
		clearDateFilter() {
			this.filterDateFrom = this.today;
			this.filterDateTo = this.today;
			this.currentPage = 1;
			this.fetchHistory();
		},

		/**
		 * Called when date filter inputs change — re-fetch history from API
		 */
		onDateFilterChange() {
			this.fetchHistory();
		},

		onPageChange(page) {
			if (page > this.maxPage) {
				page = this.maxPage;
			}
			if (page < 1) {
				page = 1;
			}

			if (page == this.currentPage) {
				return;
			}

			this.currentPage = page;

			this.fetchHistory();
		},

		// ── Manage Modal ──────────────────────────────

		/**
		 * Open manage modal for an agency
		 */
		openManageModal(agency) {
			this.manageAgency = agency;
			this.balanceAmount = '';
			this.showManageModal = true;
		},

		/**
		 * Close manage modal
		 */
		closeManageModal() {
			this.showManageModal = false;
			this.manageAgency = null;
			this.balanceAmount = '';
		},

		/**
		 * Submit credit balance adjustment.
		 * Uses addChildAgencyBalance(id, amount) — positive to add, negative to subtract.
		 * Backend returns updated balance and credit_balance in response.
		 */
		async submitBalance() {
			if (this.isSaving || !this.balanceAmount) return;

			const amount = Number(this.balanceAmount);
			if (isNaN(amount) || amount === 0) return;

			this.isSaving = true;
			try {
				const res = await addChildAgencyBalance(this.manageAgency.id, amount);

				// Update local state from server response
				if (res) {
					if (res.balance != null) this.$set(this.manageAgency, 'balance', Number(res.balance));
					if (res.credit_balance != null) this.$set(this.manageAgency, 'credit', Number(res.credit_balance));
				}

				this.balanceAmount = '';
				this.$toasted.show(this.$t('agency.credit_updated'), { duration: 3000 });
			}
			catch (err) {
				this.$log.error(err);
				const msg = err.response && err.response.status === 403
					? 'Permission denied'
					: this.$t('agency.credit_update_failed');
				this.$toasted.show(msg, { duration: 3000 });
			}
			finally {
				this.isSaving = false;
			}
		},

		// ── History ───────────────────────────────────

		/**
		 * Toggle change history panel for a specific agency
		 */
		async toggleHistory(agency) {
			if (this.historyAgencyId === agency.id) {
				this.historyAgencyId = null;
				this.changeHistory = {};
				this.currentPage = 1;
				this.filterDateFrom = this.today;
				this.filterDateTo = this.today;
				return;
			}

			this.historyAgencyId = agency.id;
			this.changeHistory = {};
			this.currentPage = 1;
			this.filterDateFrom = this.today;
			this.filterDateTo = this.today;

			await this.fetchHistory();
		},

		/**
		 * Fetch change history from API with optional date filters
		 */
		async fetchHistory() {
			if (!this.historyAgencyId) {
				return;
			}

			this.isLoadingHistory = true;
			try {
				const params = {
					page: this.currentPage,
					size: this.pageSize,
				};

				if (this.filterDateFrom) {
					params.date_from = this.filterDateFrom;
				}
				if (this.filterDateTo) {
					params.date_to = this.filterDateTo;
				}

				const response = await requestBalanceChangeHistory(this.historyAgencyId, params);
				this.changeHistory = response || {};
			}
			catch (err) {
				this.$log.error(err);
				this.changeHistory = {};
			}
			finally {
				this.isLoadingHistory = false;
			}
		},

		// ── Data Loading ─────────────────────────────

		/**
		 * Fetch agencies balance data
		 */
		async loadData() {
			this.isLoading = true;
			try {
				this.agencies = await requestAgenciesBalance();
			}
			catch (err) {
				this.$log.error(err);
				this.agencies = [];
			}
			finally {
				this.isLoading = false;
			}
		},
	},


	async mounted() {
		await this.loadData();
	},
};
</script>


<style lang="sass" scoped>
.header
	margin-bottom: 20px

.title
	font-weight: 300
	font-size: 24px
	margin-bottom: 15px

.top-panel
	display: flex
	flex-direction: row
	align-items: center
	gap: 24px
	margin-bottom: 20px

.section-title
	font-weight: 400
	font-size: 14px
	margin: 0
	color: #919191

.history-row
	background: #fafafa

.history-cell
	padding: 16px 12px

.history-section
	margin: 0

.history-header
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 12px

.date-filter
	display: flex
	align-items: center
	gap: 8px

.date-label
	font-size: 12px
	color: #919191
	text-transform: uppercase

.date-input
	padding: 5px 8px
	border: 1px solid #ddd
	border-radius: 5px
	font-size: 13px
	outline: none
	color: #232323

	&:focus
		border-color: var(--main_color, #2dc3b7)

.clear-filter-btn
	margin-left: 4px

.history-scroll
	max-height: 300px
	overflow-y: auto
	border: 1px solid #e9e9e9
	border-radius: 5px

	.paginate
		display: flex
		justify-content: space-between

		a
			display: inline-block
			margin: 10px 10px 20px
			text-decoration: none
			font-size: 200%
			color: black
			cursor: pointer

			&:hover
				text-decoration: underline

			&.hidden
				visibility: hidden

.balance-table,
.history-table
	width: auto
	border-collapse: collapse

	th
		text-align: left
		white-space: nowrap
		padding: 10px 24px 10px 12px
		font-size: 11px
		text-transform: uppercase
		color: #919191
		letter-spacing: 0.5px

	td
		padding: 12px 24px 12px 12px
		white-space: nowrap

.history-table
	width: 100%

	thead
		position: sticky
		top: 0
		background: #fff
		z-index: 1

.agency-name,
.col-name
	font-weight: 500

.agency-balance,
.agency-prepaid-spent,
.agency-credit,
.agency-credit-spent,
.col-value
	white-space: nowrap

.col-field
	text-transform: capitalize

.col-user
	color: #555

.col-date
	color: #919191
	font-size: 12px

.agency-actions
	white-space: nowrap
	text-align: right

.action-btn
	margin-left: 8px

	&:first-child
		margin-left: 0

.loading-message,
.empty-message
	text-align: center
	padding: 40px
	color: #919191
	font-size: 14px

// ── Manage Modal ──────────────────────────────
.manage-modal
	padding: 30px 40px
	box-sizing: border-box
	max-width: 600px
	width: 100%

.manage-modal__header
	display: flex
	align-items: center
	margin-bottom: 30px

	& > *
		margin-left: 15px

		&:first-child
			margin-left: 0

.manage-modal__icon
	--ni-icon-sign: var(--main_color)
	width: 26px
	height: 26px
	flex-shrink: 0

.manage-modal__title
	font-size: 16px
	line-height: 20px
	font-weight: normal

.manage-modal__actions
	display: flex
	align-items: center
	justify-content: flex-end
	margin-left: auto

.manage-modal__current
	display: flex
	gap: 40px
	margin-bottom: 30px
	padding: 16px 20px
	background: #f7f7f7
	border-radius: 8px

.manage-modal__stat
	display: flex
	flex-direction: column

.manage-modal__label
	font-size: 11px
	text-transform: uppercase
	color: #919191
	letter-spacing: 0.5px
	margin-bottom: 4px

.manage-modal__value
	font-size: 18px
	font-weight: 500

.manage-modal__section
	margin-bottom: 24px

.manage-modal__section-title
	font-size: 14px
	font-weight: 500
	margin-bottom: 6px

.manage-modal__hint
	font-size: 12px
	color: #919191
	margin-bottom: 10px

.manage-modal__input-row
	display: flex
	align-items: center
	gap: 12px

.manage-modal__input
	width: 200px
	padding: 7px 12px
	border: 1px solid #ccc
	border-radius: 5px
	font-size: 13.5px
	outline: none

	&:focus
		border-color: var(--main_color, #2dc3b7)
</style>
